import { TemplateResult, html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { BlocksChangeEvent } from '../../events';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../icon/icons/calendar';
import { BlocksInputField } from '../input-field/input-field';
import type { CombinedInputSize, CombinedInputStatus } from './combined-input.model';
import styles from './combined-input.styles';

/**
 * @slot field-note - for custom field notes
 * @slot error - for custom error notes
 * @fires bl-change - emitted on change from or to date
 * @csspart base - The component's base wrapper.
 * @csspart wrapper - The component's wrapper.
 * @csspart start-input-container - The component's start-input-container.
 * @csspart start-input - The component's start-input wrapper.
 * @csspart start-after - The component's start-after slot.
 * @csspart end-input-container - The component's end-input-container wrapper.
 * @csspart end-input - The component's end-input wrapper.
 * @csspart end-after - The component's end-after wrapper.
 */
export class BlocksCombinedInput extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * The unique id of the select field
   */
  @property({ type: String })
  fieldId: string | undefined;

  /**
   * The Start Date field's label
   */
  @property({ type: String })
  startDateLabel = 'Start Date';

  /**
   * The End Date field's label
   */
  @property({ type: String })
  endDateLabel = 'End Date';

  /**
   * The field's name attribute
   */
  @property({ type: String })
  name: string | undefined;

  /**
   * The field's value attribute
   */
  @property({ type: String })
  value: string | undefined;

  /**
   * The field's placeholder attribute
   */
  @property({ type: String })
  placeholder = 'mm/dd/yyyy';

  /**
   * The  field's required attribute
   */
  @property({ type: Boolean })
  required = false;

  /**
   * The select field's disabled attribute
   */
  @property({ type: Boolean })
  disabled: boolean | undefined;

  /**
   * AutoFocus attribute
   */
  @property({ type: Boolean, reflect: true })
  autoFocus: boolean | undefined;

  /**
   * size
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**lg** increases the datepicker height with label inside</li>
   * <li>**md** reduce the datepicker height with label outside </li>
   * <li>**sm** reduce  the datepicker height without label</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: CombinedInputSize = 'lg';

  /**
   * Status of datepicker input field
   */
  @property({ type: String })
  status: CombinedInputStatus;

  /**
   * Readonly attribute
   */
  @property({ type: Boolean })
  isReadonly: boolean | undefined;

  /**
   * Hide label?
   */
  @property({ type: Boolean })
  hideLabel: boolean | undefined;
  /**
   * Loading state
   * 1) Adds a loader if is true
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * Notation is used for setting required or optional label
   */
  @property({ type: String })
  notation: string | undefined;

  connectedCallback() {
    super.connectedCallback();
    this.fieldId = this.fieldId || nanoid();
  }

  /**
   * Change output binding
   * @param $event focused element
   */
  private handleOnChange(e: Event) {
    const targetInput = e.target as Element as BlocksInputField;
    const value = targetInput.value;
    this.dispatch({ eventName: 'bl-change', detailObj: { value } }) as BlocksChangeEvent;
  }

  /**
   * Handle on input keydown
   * 1) If key selected is enter or spacebar, toggle the active state
   */
  private handleKeyDown(e: KeyboardEvent) {
    const targetInput = e.target as Element as BlocksInputField;
    targetInput.shadowRoot?.querySelector('input')?.focus();
    if (e.key === KEYS.ENTER || e.key === KEYS.SPACE) {
      this.toggleActive(e);
    }
  }

  /**
   * Show when focused in the input field
   */
  private toggleActive(e: Event) {
    const inputs = this.shadowRoot?.querySelectorAll('cat-input-field');

    inputs?.forEach((input: BlocksInputField) => {
      input.isActive = false;
    });

    const targetInput = e.currentTarget as Element as BlocksInputField;
    targetInput.isActive = true;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-combined-input');

    return html`
      <div part="base" class="${componentClassNames}">
        <div part="wrapper" class="cat-c-combined-input__wrapper">
          <div part="start-input-container" class="cat-c-combined-input__input-container">
            <cat-input-field
              part="start-input"
              exportparts=${this.getExportParts('input-field-start', 'input-field')}
              class="cat-c-combined-input__input"
              label="${this.startDateLabel}"
              ?hideLabel="${this.hideLabel}"
              ?readonly="${this.isReadonly}"
              id="${ifDefined(this.fieldId)}"
              name="${ifDefined(this.name)}"
              value="${ifDefined(this.value)}"
              ?required="${this.required}"
              ?disabled="${this.disabled}"
              placeholder="${ifDefined(this.placeholder)}"
              @keydown=${this.handleKeyDown}
              ?autoFocus="${this.autoFocus}"
              @click=${this.toggleActive}
              @input=${this.handleOnChange}
              size=${this.size}
              status=${ifDefined(this.status)}
              .notation=${this.notation}
            >
              <cat-icon-calendar part="start-after" exportparts=${this.getExportParts('calendar', 'icon')} slot="after"></cat-icon-calendar>
            </cat-input-field>
          </div>
          <div part="end-input-container" class="cat-c-combined-input__input-container">
            <cat-input-field
              part="end-input"
              exportparts=${this.getExportParts('input-field-end', 'input-field')}
              class="cat-c-combined-input__input"
              label="${this.endDateLabel}"
              ?hideLabel="${this.hideLabel}"
              ?readonly="${this.isReadonly}"
              name="${ifDefined(this.name)}"
              value="${ifDefined(this.value)}"
              ?required="${this.required}"
              ?disabled="${this.disabled}"
              placeholder="${ifDefined(this.placeholder)}"
              @keydown=${this.handleKeyDown}
              @click=${this.toggleActive}
              @input=${this.handleOnChange}
              .size=${this.size}
              .status=${this.status}
              .notation=${this.notation}
            >
              <cat-icon-calendar part="end-after" exportparts=${this.getExportParts('calendar', 'icon')} slot="after"></cat-icon-calendar>
            </cat-input-field>
          </div>
        </div>
      </div>
    `;
  }
}

if (customElements.get('cat-combined-input') === undefined) {
  customElements.define('cat-combined-input', BlocksCombinedInput);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-combined-input': BlocksCombinedInput;
  }
}
